import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';

import { PictureBase64Model } from './models/picture-base64.model';
import { ImagePreview } from './models/image-preview.model';


@Component({
  selector: 'app-picture-upload',
  templateUrl: './picture-upload.component.html',
  styleUrls: ['./picture-upload.component.scss'],
  standalone: false
})
export class PictureUploadComponent implements OnInit, OnChanges {
  /**File index*/
  readonly fIndex = 0;

  @ViewChild('input', { read: ElementRef }) fileInput: ElementRef;

  @Input()
  title: string;

  @Input()
  subtitle: string;

  @Input()
  action: string;

  @Input()
  iconPlaceholder: string = 'icon-image-photo';

  @Input()
  iconAnexo: string = 'anexo-icon';

  @Input()
  customBox: boolean = false;

  @Input()
  customClass: string;

  @Input()
  text: string = 'selecione a imagem';

  @Input()
  extensions: string;

  /**Image preview*/
  @Input() previews: ImagePreview[] = [];

  @Output() base64: EventEmitter<PictureBase64Model[]> = new EventEmitter<PictureBase64Model[]>();

  @Output() coverChanged: EventEmitter<number> = new EventEmitter<number>();

  @Output() previewsChange = new EventEmitter<ImagePreview[]>();


  /**Value in megabytes */
  @Input() maxSize = 5;

  coverIndex: number = 0;

  imageToReplace: number | null = null;


  constructor(
    public domSanitizationService: DomSanitizer,
    public toastrService: ToastrService
  ) { }

  ngOnInit(): void {
    if (this.previews && this.previews.length) {
      const primaryIndex = this.previews.findIndex(p => p.primary);
      this.coverIndex = primaryIndex >= 0 ? primaryIndex : 0;
    }
  }

  ngOnChanges(): void {
    if (this.previews && this.previews.length) {
      const primaryIndex = this.previews.findIndex(p => p.primary);
      this.coverIndex = primaryIndex >= 0 ? primaryIndex : 0;
    }
  }

  /**
  * @description Exibe o preview das imagens selecionadas no input de arquivo,
  * convertendo-as para Base64 e adicionando ao array de previews. 
  * Também trata os casos de substituição de imagem existente, limite máximo 
  * de 5 imagens e validação de tamanho (MB).
  *
  * - Se `this.imageToReplace` estiver definido, substitui a imagem existente.
  * - Caso contrário, adiciona novas imagens ao preview (até 5).
  * - Emite o estado atualizado após leitura dos arquivos.
  * - Reseta o input de arquivo para permitir reupload da mesma imagem.
  *
  * @author Pedro Arthur
  * @param event Evento de mudança do input de arquivo contendo a lista de arquivos selecionados
  */
  showPreview(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const filesToProcess = Array.from(files).slice(0, 5);
    let filesRead = 0;

    //
    if (this.imageToReplace !== null) {
      const file = filesToProcess[0];
      if (!file) return;

      if (file.size / 1024 / 1024 >= this.maxSize) {
        this.toastrService.error(
          `A imagem "${file.name}" é maior que ${this.maxSize}MB`,
          'Erro!'
        );
        this.imageToReplace = null;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previews[this.imageToReplace!] = {
          image: e.target.result,
          url: '',
          primary: this.imageToReplace === this.coverIndex,
        };
        this.emitCurrentState();
        this.imageToReplace = null;
      };
      reader.readAsDataURL(file);
      return;
    }


    if (filesToProcess.length + this.previews.length > 5) {
      this.toastrService.warning('Máximo de 5 imagens permitidas.', 'Aviso');
      return;
    }


    filesToProcess.forEach((file: File) => {
      if (file.size / 1024 / 1024 >= this.maxSize) {
        this.toastrService.error(
          `A imagem "${file.name}" é maior que ${this.maxSize}MB`,
          'Erro!'
        );
        filesRead++;
        if (filesRead === filesToProcess.length) {
          this.emitCurrentState();
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const preview: ImagePreview = {
          image: e.target.result,
          url: '',
          primary: this.previews.length === 0 && filesRead === 0,
        };

        this.previews.push(preview);
        filesRead++;
        if (filesRead === filesToProcess.length) {
          this.emitCurrentState();
        }
      };
      reader.readAsDataURL(file);
    });
  }



  /**
   * @description Define a imagem informada pelo índice como a capa (cover) do produto.
   * Atualiza o índice de capa e emite o evento de mudança.
   *
   * @author Pedro Arthur
   * @param index Índice da imagem no array de previews que será definida como capa
   */
  setAsCover(index: number) {
    this.coverIndex = index;
    this.coverChanged.emit(this.coverIndex);
    this.emitCurrentState();
  }

  /**
  * @description Remove uma imagem do array de previews e ajusta o índice da capa.
  * Também reseta o input de arquivo para permitir reupload da mesma imagem.
  *
  * @author Pedro Arthur
  * @param index Índice da imagem no array de previews que será removida
  */
  removeImage(index: number) {
    this.previews.splice(index, 1);

    if (this.coverIndex === index) {
      this.coverIndex = 0;
    } else if (this.coverIndex > index) {
      this.coverIndex--;
    }

    this.emitCurrentState();

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
 * @description Permite substituir uma imagem existente no array de previews,
 * acionando o input de arquivo para seleção de nova imagem.
 *
 * @author Pedro Arthur
 * @param index Índice da imagem no array de previews que será substituída
 */
  replaceImage(index: number) {
    this.imageToReplace = index;
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  /**
   * Obter a imagem na representação base64 e enviar para o parent(component)
   *
   * @author Alysson Cordeiro
   *
   * @param file Representação da imagem
   */
  getBase64(file) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const pictureBase64: PictureBase64Model = { data: reader.result };
      this.base64.emit([pictureBase64]);
    };
  }

  /**
 * @description Permite ao usuário editar uma imagem já existente no preview,
 * substituindo-a por um novo arquivo selecionado via input dinâmico.
 *
 * @author Pedro Arthur
 * @param index Índice da imagem no array de previews que será substituída
 */
  editImage(index: number) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = this.extensions;

    fileInput.onchange = (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = e => {
          this.previews[index] = {
            image: e.target?.result as string,
            url: '',
            primary: this.previews[index].primary,
          };
          this.emitCurrentState();
        };
        reader.readAsDataURL(file);
      }
    };

    fileInput.click();
  }



  click() {
    this.fileInput.nativeElement.value = '';
    const event = new MouseEvent('click', { bubbles: true });
    this.fileInput.nativeElement.dispatchEvent(event);
  }

  emitCurrentState() {
    this.previews = this.previews.map((p, idx) => ({
      ...p,
      primary: idx === this.coverIndex,
    }));

    const base64List = this.previews.map(p => ({
      data: p.image || p.url,
      primary: p.primary,
    }));

    this.base64.emit(base64List);
    this.coverChanged.emit(this.coverIndex);
  }


  getSantizeUrl(url: string) {
    if (url) {
      return this.domSanitizationService.bypassSecurityTrustUrl(url);
    }
  }
}
